import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Input, message, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { decodeBase32Secret, generateTotp } from '../../utils/totp';

export function MfaCodeGenerator(): JSX.Element {
  const [setupKey, setSetupKey] = useState('');
  const [current, setCurrent] = useState<{
    code: string;
    remaining: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCurrent(null);
    setError(null);
    if (!setupKey.trim()) {
      return;
    }
    let secret: Uint8Array;
    try {
      secret = decodeBase32Secret(setupKey);
    } catch (err) {
      setError((err as Error).message);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const now = Date.now() / 1000;
      const { code, expiresAt } = await generateTotp(secret, now);
      if (cancelled) {
        return;
      }
      setCurrent({ code, remaining: Math.ceil(expiresAt - now) });
      timer = setTimeout(() => void tick(), 1000);
    };
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [setupKey]);

  const copy = async () => {
    if (!current) {
      return;
    }
    try {
      await navigator.clipboard.writeText(current.code);
      void message.success('MFA code copied.');
    } catch {
      setError('Could not copy the MFA code. Please copy it manually.');
    }
  };

  return (
    <Card style={{ textAlign: 'left', height: '100%' }}>
      <Typography.Title level={4}>MFA code generator</Typography.Title>
      <Typography.Paragraph type="secondary">
        No client configuration yet but have the authenticator code? Paste it
        here to generate the 6-digit login code. The key stays in your browser
        and is never saved.
      </Typography.Paragraph>
      <Input.Password
        autoComplete="off"
        onChange={event => setSetupKey(event.target.value)}
        placeholder="Authenticator setup key"
        size="large"
        value={setupKey}
      />
      <div style={{ marginTop: 16, minHeight: 40 }}>
        {error && <Alert message={error} showIcon type="error" />}
        {current && (
          <Space size="middle" wrap>
            <Typography.Text
              strong
              style={{
                fontFamily: 'monospace',
                fontSize: 28,
                letterSpacing: 4,
              }}
            >
              {current.code}
            </Typography.Text>
            <Button icon={<CopyOutlined />} onClick={() => void copy()}>
              Copy code
            </Button>
            <Typography.Text type="secondary">
              Expires in {current.remaining}s
            </Typography.Text>
          </Space>
        )}
      </div>
    </Card>
  );
}
